/**
 * Recruitment Analytics — company-wide hiring dashboard.
 *
 * Shows: funnel, conversions, source mix, time-to-fill, top referrers,
 * rejection reasons, period KPIs. Window selectable (30/60/90/180/365 days).
 */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Briefcase, Users, UserPlus, Clock, ArrowDown, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const STAGE_LABEL = {
  applied: "Applied",
  screening: "Screening",
  shortlisted: "Shortlisted",
  interview: "Interview",
  offer_pending: "Offer pending",
  offer_sent: "Offer sent",
  offer_accepted: "Offer accepted",
  hired: "Hired",
  rejected: "Rejected",
};

const STAGE_COLOR = {
  applied: "bg-zinc-200 text-zinc-700",
  screening: "bg-blue-100 text-blue-700",
  shortlisted: "bg-violet-100 text-violet-700",
  interview: "bg-amber-100 text-amber-700",
  offer_pending: "bg-orange-100 text-orange-700",
  offer_sent: "bg-orange-200 text-orange-800",
  offer_accepted: "bg-emerald-100 text-emerald-700",
  hired: "bg-emerald-600 text-white",
  rejected: "bg-rose-100 text-rose-700",
};

const SOURCE_LABEL = {
  careers_page: "Careers page",
  referral: "Employee referral",
  linkedin: "LinkedIn",
  naukri: "Naukri",
  indeed: "Indeed",
  agency: "Agency",
  other: "Other",
};

export default function RecruitmentAnalytics() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/recruitment/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <AppShell><div className="p-8 text-zinc-500">Loading…</div></AppShell>;
  if (!data) return null;

  const maxFunnel = Math.max(...data.funnel.map(f => f.count), 1);
  const totalReferrals = data.source_mix.find(s => s.source === "referral")?.count || 0;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="recruit-analytics-page">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <ChartBar size={22}/> Recruiting Analytics
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Pipeline, conversion, sources & velocity — last {days} days</p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
            data-testid="window-selector"
          >
            {[30, 60, 90, 180, 365].map(d => (
              <option key={d} value={d}>Last {d} days</option>
            ))}
          </select>
        </header>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KPI icon={Briefcase} label="Open requisitions" value={data.totals.open_requisitions}
            sub={`${data.totals.open_openings} openings`} color="bg-violet-50 border-violet-200"/>
          <KPI icon={Users} label="Active candidates" value={data.totals.candidates_active}
            sub={`${data.totals.candidates_new_window} new this period`} color="bg-blue-50 border-blue-200"/>
          <KPI icon={UserPlus} label="Hires this period" value={data.totals.hires_window}
            sub={`${data.totals.candidates_total} total in pipeline`} color="bg-emerald-50 border-emerald-200"/>
          <KPI icon={Clock} label="Avg time-to-fill"
            value={data.totals.avg_time_to_fill_days != null ? `${data.totals.avg_time_to_fill_days}d` : "—"}
            sub="closed reqs in window" color="bg-amber-50 border-amber-200"/>
        </div>

        {/* Funnel + Conversions */}
        <Card className="p-6" data-testid="funnel-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold">Pipeline funnel</h2>
            <div className="text-xs text-zinc-500">{data.totals.candidates_total} candidates total</div>
          </div>
          <div className="space-y-2">
            {data.funnel.map(f => {
              const width = `${(f.count / maxFunnel) * 100}%`;
              const color = STAGE_COLOR[f.stage] || STAGE_COLOR.applied;
              return (
                <div key={f.stage} className="flex items-center gap-3 text-sm" data-testid={`funnel-${f.stage}`}>
                  <div className="w-32 text-right text-zinc-700">{STAGE_LABEL[f.stage] || f.stage}</div>
                  <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden relative">
                    <div className={`h-full ${color} flex items-center px-2 text-xs font-medium`} style={{ width }}>
                      {f.count > 0 && f.count}
                    </div>
                  </div>
                  <div className="w-10 text-right font-medium">{f.count}</div>
                </div>
              );
            })}
          </div>
          {data.conversions.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Stage-to-stage conversion</div>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                {data.conversions.map((c, i) => (
                  <div key={i} className="text-center p-2 border rounded">
                    <div className="text-lg font-semibold">{c.rate}%</div>
                    <div className="text-[10px] text-zinc-500 leading-tight">
                      {STAGE_LABEL[c.from]?.split(" ")[0]} → {STAGE_LABEL[c.to]?.split(" ")[0]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Source mix */}
          <Card className="p-6" data-testid="source-card">
            <h2 className="text-base font-semibold mb-3">Source mix</h2>
            {data.source_mix.length === 0 ? (
              <div className="text-sm text-zinc-500 text-center py-6">No data yet.</div>
            ) : (
              <div className="space-y-2">
                {data.source_mix.map(s => {
                  const max = Math.max(...data.source_mix.map(x => x.count));
                  const width = `${(s.count / max) * 100}%`;
                  return (
                    <div key={s.source} className="flex items-center gap-2 text-sm">
                      <div className="w-32 text-zinc-600">{SOURCE_LABEL[s.source] || s.source}</div>
                      <div className="flex-1 h-5 bg-zinc-100 rounded overflow-hidden">
                        <div className="h-full bg-violet-500 flex items-center px-2 text-xs text-white" style={{ width }}>
                          {s.count > 0 && s.count}
                        </div>
                      </div>
                      <div className="w-10 text-right text-zinc-700">{s.count}</div>
                    </div>
                  );
                })}
                <div className="mt-3 pt-3 border-t text-xs text-zinc-500">
                  Referrals: <strong className="text-zinc-700">{totalReferrals}</strong>{" "}
                  ({data.totals.candidates_total > 0
                    ? Math.round((totalReferrals / data.totals.candidates_total) * 100)
                    : 0}% of pipeline)
                </div>
              </div>
            )}
          </Card>

          {/* Top referrers */}
          <Card className="p-6" data-testid="referrers-card">
            <h2 className="text-base font-semibold mb-3">Top referrers</h2>
            {data.top_referrers.length === 0 ? (
              <div className="text-sm text-zinc-500 text-center py-6">No referrals yet — encourage employees to refer.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-zinc-500">
                  <tr className="border-b"><th className="text-left pb-2">Employee</th><th className="text-right pb-2">Submitted</th><th className="text-right pb-2">Hired</th></tr>
                </thead>
                <tbody>
                  {data.top_referrers.map(r => (
                    <tr key={r.employee_id} className="border-b last:border-0">
                      <td className="py-2">{r.name}</td>
                      <td className="py-2 text-right">{r.submissions}</td>
                      <td className="py-2 text-right font-medium text-emerald-700">{r.hires}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        {/* Rejection reasons */}
        {data.rejection_breakdown.length > 0 && (
          <Card className="p-6" data-testid="reject-card">
            <h2 className="text-base font-semibold mb-3">Top rejection reasons</h2>
            <div className="space-y-1">
              {data.rejection_breakdown.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                  <span className="text-zinc-700">{r.reason}</span>
                  <Badge variant="outline">{r.count}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

function KPI({ icon: Icon, label, value, sub, color }) {
  return (
    <div className={`p-4 rounded border ${color}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wide text-zinc-600">{label}</div>
        <Icon size={16} className="text-zinc-500"/>
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
